import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../shared/context/ThemeContext';
import './PresentationPage.css';

const MOCK = {
  kpis: {
    totalTx: 1284, successRate: 93.3, successCount: 1198,
    avgResponse: 142, openIncidents: 3, criticalIncidents: 1,
    activeAlerts: 7, criticalAlerts: 2, pirPending: 4,
  },
  components: [
    { name: 'payments-service', health: 72,  requests: 412, color: '#ff4d6d', dim: 'rgba(255,77,109,0.1)'  },
    { name: 'auth-service',     health: 88,  requests: 287, color: '#ffb830', dim: 'rgba(255,184,48,0.1)'  },
    { name: 'api-gateway',      health: 97,  requests: 334, color: '#00d97e', dim: 'rgba(0,217,126,0.1)'   },
    { name: 'db-service',       health: 84,  requests: 198, color: '#ffb830', dim: 'rgba(255,184,48,0.1)'  },
    { name: 'notification-svc', health: 99,  requests: 53,  color: '#00d97e', dim: 'rgba(0,217,126,0.1)'   },
  ],
  hourlyVolume: [12,8,5,4,6,14,28,55,72,84,91,88,76,82,79,88,94,102,97,85,74,61,44,30],
  incidents: [
    { id: 'INC-0041', title: 'Payment Gateway Timeout',      service: 'payments-service', severity: 'CRITICAL', status: 'IN_PROGRESS', ago: '2h 14m ago' },
    { id: 'INC-0040', title: 'Auth Service Latency Spike',   service: 'auth-service',     severity: 'HIGH',     status: 'IN_PROGRESS', ago: '4h 52m ago' },
    { id: 'INC-0039', title: 'DB Connection Pool Exhausted', service: 'db-service',       severity: 'HIGH',     status: 'NEW',         ago: '6h 10m ago' },
  ],
  alerts: [
    { title: 'High error rate on payments-service',         service: 'payments-service', severity: 'CRITICAL', state: 'ALARM' },
    { title: 'P95 latency breached — auth-service',         service: 'auth-service',     severity: 'CRITICAL', state: 'ALARM' },
    { title: 'DB connection pool usage above 80%',          service: 'db-service',       severity: 'HIGH',     state: 'ALARM' },
    { title: 'Anomalous request volume — api-gateway',      service: 'api-gateway',      severity: 'HIGH',     state: 'ALARM' },
    { title: 'Memory utilisation spike — notification-svc', service: 'notification-svc', severity: 'MEDIUM',   state: 'ALARM' },
  ],
  prEvents: [
    { repo: 'payments-service', number: 214, title: 'feat: retry logic',     author: 'dev-tom',   status: 'PASSED',  ago: '32m ago' },
    { repo: 'auth-service',     number: 88,  title: 'fix: token refresh',    author: 'dev-priya', status: 'FAILED',  ago: '1h ago'  },
    { repo: 'api-gateway',      number: 301, title: 'refactor: middleware',   author: 'dev-sam',   status: 'PENDING', ago: '2h ago'  },
    { repo: 'db-service',       number: 55,  title: 'chore: index optimise', author: 'dev-lee',   status: 'PASSED',  ago: '3h ago'  },
    { repo: 'payments-service', number: 215, title: 'fix: idempotency key',  author: 'dev-tom',   status: 'PASSED',  ago: '4h ago'  },
  ],
  prTrend: {
    labels: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
    passed: [5,7,4,6,3,8,4],
    failed: [1,0,2,1,3,0,1],
  },
};

const SLIDE_DURATION = 10000;

// ── helpers ────────────────────────────────────────────────────────────────────
const sevColor = s => { const u=(s||'').toUpperCase(); if(u==='CRITICAL') return '#ff4d6d'; if(u==='HIGH') return '#ff7c43'; if(u==='MEDIUM') return '#ffb830'; return '#00d97e'; };
const sevClass  = s => { const u=(s||'').toUpperCase(); if(u==='CRITICAL') return 'crit'; if(u==='HIGH') return 'high'; if(u==='MEDIUM') return 'med'; return 'ok'; };
const stClass   = s => { const u=(s||'').toUpperCase(); if(u==='PASSED') return 'ok'; if(u==='FAILED') return 'crit'; return 'med'; };

// ── Animated counter ───────────────────────────────────────────────────────────
function Counter({ value, suffix = '', duration = 1400 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = parseFloat(value) || 0;
    const start = performance.now();
    const step = now => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setDisplay(+(eased * target).toFixed(1));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value, duration]);
  const fmt = Number.isInteger(parseFloat(value)) ? Math.round(display).toLocaleString() : display;
  return <>{fmt}{suffix}</>;
}

// ── Floating particle background ───────────────────────────────────────────────
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      opacity: Math.random() * 0.5 + 0.1,
      color: ['#bf14cc','#d94fe0','#00d97e','#ff4d6d','#ffb830'][Math.floor(Math.random()*5)],
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="pres-particles" />;
}

// ── Big tick ring with glow ────────────────────────────────────────────────────
function GlowRing({ pct, color, dim, size = 200, label, sublabel, light }) {
  const [disp, setDisp] = useState(0);
  useEffect(() => {
    setDisp(0);
    const dur = 1400, start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / dur, 1);
      setDisp((1 - Math.pow(1 - p, 3)) * pct);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [pct]);
  const cx = size/2, cy = size/2, r = size*0.42;
  const ticks = 60, active = Math.round((disp/100)*ticks);
  const innerR = r - size*0.06;
  return (
    <div className="pres-glow-ring-wrap" style={{ '--rc': color }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ filter: `drop-shadow(0 0 12px ${color}55)` }}>
        {Array.from({ length: ticks }, (_, i) => {
          const ang = (i/ticks)*360 - 90, rad = ang*Math.PI/180;
          const x1=cx+innerR*Math.cos(rad), y1=cy+innerR*Math.sin(rad);
          const x2=cx+r*Math.cos(rad),      y2=cy+r*Math.sin(rad);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={i<active?color:(light?'rgba(0,0,0,0.12)':'rgba(255,255,255,0.12)')} strokeWidth={size*0.022} strokeLinecap="round" />;
        })}
      </svg>
      <div className="pres-glow-ring-center">
        <div className="pres-glow-ring-pct" style={{ color }}><Counter value={pct} suffix="%" /></div>
        {label && <div className="pres-glow-ring-label">{label}</div>}
        {sublabel && <div className="pres-glow-ring-sublabel">{sublabel}</div>}
      </div>
    </div>
  );
}

// ── Full-width volume chart ────────────────────────────────────────────────────
function VolumeChart({ data }) {
  const ref = useRef(null);
  const [w, setW] = useState(800);
  const [h, setH] = useState(300);
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setW(r.width || 800);
      setH(r.height || 300);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el); return () => ro.disconnect();
  }, []);
  useEffect(() => {
    setAnimPct(0);
    const dur = 1200, start = performance.now();
    const tick = now => {
      const p = Math.min((now-start)/dur, 1);
      setAnimPct(1 - Math.pow(1-p, 3));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [data]);

  const H=h, pL=42, pR=16, pT=16, pB=36;
  const cW=w-pL-pR, cH=H-pT-pB;
  const max=Math.max(...data,1), n=data.length;
  const aData = data.map(v => v*animPct);
  const toX = i => pL+(i/(n-1))*cW;
  const toY = v => pT+(1-v/max)*cH;
  const xs=aData.map((_,i)=>toX(i)), ys=aData.map(v=>toY(v));
  const path=xs.reduce((d,x,i)=>{ if(i===0) return `M${x},${ys[i]}`; const cpx=(xs[i-1]+x)/2; return d+` C${cpx},${ys[i-1]} ${cpx},${ys[i]} ${x},${ys[i]}`; },'');
  const area=`${path} L${xs[n-1]},${pT+cH} L${xs[0]},${pT+cH} Z`;
  const gridFs=[0,0.25,0.5,0.75,1];
  const lbls=[0,4,8,12,16,20,23];

  return (
    <div ref={ref} style={{width:'100%',height:'100%'}}>
      <svg width={w} height={H} style={{display:'block',overflow:'visible'}}>
        <defs>
          <linearGradient id="presVolGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#bf14cc" stopOpacity="0.6"/>
            <stop offset="60%" stopColor="#bf14cc" stopOpacity="0.15"/>
            <stop offset="100%" stopColor="#bf14cc" stopOpacity="0"/>
          </linearGradient>
          <filter id="presGlow">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {gridFs.map(f=>{
          const y=pT+(1-f)*cH, label=f===0?'0':`${Math.round(f*max)}`;
          return <g key={f}>
            <line x1={pL} y1={y} x2={w-pR} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1}/>
            <text x={pL-6} y={y+4} textAnchor="end" fontSize={11} fill="#6b7a99" fontFamily="inherit">{label}</text>
          </g>;
        })}
        {lbls.map(i=>(
          <text key={i} x={toX(i)} y={H-8} textAnchor="middle" fontSize={11} fill="#6b7a99" fontFamily="inherit">
            {String(i).padStart(2,'0')}:00
          </text>
        ))}
        <path d={area} fill="url(#presVolGrad)"/>
        <path d={path} fill="none" stroke="#bf14cc" strokeWidth={3} strokeLinejoin="round" filter="url(#presGlow)"/>
        {xs.map((x,i)=> data[i]>0 && (
          <circle key={i} cx={x} cy={ys[i]} r={3} fill="#bf14cc" stroke="#080b12" strokeWidth={1.5}
            style={{filter:'drop-shadow(0 0 4px #bf14cc)'}}/>
        ))}
      </svg>
    </div>
  );
}

// ── PR trend chart ─────────────────────────────────────────────────────────────
function PrTrendChart({ trend }) {
  const ref = useRef(null);
  const [w, setW] = useState(500);
  const [h, setH] = useState(220);
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setW(r.width || 500);
      setH(r.height || 220);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el); return () => ro.disconnect();
  }, []);
  useEffect(() => {
    setAnimPct(0);
    const dur=1200, start=performance.now();
    const tick=now=>{ const p=Math.min((now-start)/dur,1); setAnimPct(1-Math.pow(1-p,3)); if(p<1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }, [trend]);

  const H=h, pL=28, pR=24, pT=14, pB=30;
  const cW=w-pL-pR, cH=H-pT-pB;
  const n=trend.labels.length;
  const max=Math.max(...trend.passed,...trend.failed,1);
  const toX=i=>pL+(n===1?cW/2:(i/(n-1))*cW);
  const toY=v=>pT+(1-v/max)*cH;
  const aPass=trend.passed.map(v=>v*animPct), aFail=trend.failed.map(v=>v*animPct);
  const smooth=vals=>vals.map((v,i)=>{ const x=toX(i),y=toY(v); if(i===0) return `M${x},${y}`; const cpx=(toX(i-1)+x)/2; return ` C${cpx},${toY(vals[i-1])} ${cpx},${y} ${x},${y}`; }).join('');
  const pp=smooth(aPass), fp=smooth(aFail);
  const area=p=>`${p} L${toX(n-1)},${pT+cH} L${toX(0)},${pT+cH} Z`;

  return (
    <div ref={ref} style={{width:'100%',height:'100%'}}>
      <svg width={w} height={H} style={{display:'block',overflow:'visible'}}>
        <defs>
          <linearGradient id="ppG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00d97e" stopOpacity="0.35"/><stop offset="100%" stopColor="#00d97e" stopOpacity="0"/></linearGradient>
          <linearGradient id="pfG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ff4d6d" stopOpacity="0.25"/><stop offset="100%" stopColor="#ff4d6d" stopOpacity="0"/></linearGradient>
        </defs>
        {[0,0.5,1].map(f=><line key={f} x1={pL} y1={pT+(1-f)*cH} x2={w-pR} y2={pT+(1-f)*cH} stroke="rgba(255,255,255,0.06)" strokeWidth={1}/>)}
        <path d={area(pp)} fill="url(#ppG)"/>
        <path d={area(fp)} fill="url(#pfG)"/>
        <path d={pp} fill="none" stroke="#00d97e" strokeWidth={2.5} style={{filter:'drop-shadow(0 0 6px #00d97e88)'}}/>
        <path d={fp} fill="none" stroke="#ff4d6d" strokeWidth={2.5} style={{filter:'drop-shadow(0 0 6px #ff4d6d88)'}}/>
        {trend.labels.map((lbl,i)=>(
          <g key={i}>
            <circle cx={toX(i)} cy={toY(aPass[i])} r={5} fill="#00d97e" stroke="#080b12" strokeWidth={2}/>
            <circle cx={toX(i)} cy={toY(aFail[i])} r={5} fill="#ff4d6d" stroke="#080b12" strokeWidth={2}/>
            <text x={toX(i)} y={H-8} textAnchor="middle" fontSize={11} fill="#6b7a99" fontFamily="inherit">{lbl}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── Animated segbar ────────────────────────────────────────────────────────────
function SegBar({ pct, color, dim, height=12, animPct=null }) {
  const [ownAnim, setOwnAnim] = useState(0);
  useEffect(() => {
    if (animPct !== null) return; // controlled externally
    setOwnAnim(0);
    const dur=900, start=performance.now();
    const tick=now=>{ const p=Math.min((now-start)/dur,1); setOwnAnim((1-Math.pow(1-p,3))*pct); if(p<1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  }, [pct, animPct]);
  const anim = animPct !== null ? animPct * pct : ownAnim;
  const segs=50, segW=5, gap=2, totalW=segs*segW+(segs-1)*gap;
  const active=Math.round((anim/100)*segs);
  return (
    <svg viewBox={`0 0 ${totalW} ${height}`} preserveAspectRatio="none" style={{width:'100%',height}}>
      {Array.from({length:segs},(_,i)=>(
        <rect key={i} x={i*(segW+gap)} y={0} width={segW} height={height} rx={3}
          fill={i<active?color:dim}
          style={i<active?{filter:`drop-shadow(0 0 3px ${color}88)`}:{}}/>
      ))}
    </svg>
  );
}

// ── SLIDES ─────────────────────────────────────────────────────────────────────

function KpiIcon({ color, children, light }) {
  const fill = light ? '#e8edf5' : '#0d1524';
  return (
    <svg width="160" height="160" viewBox="0 0 247 247" fill="none" xmlns="http://www.w3.org/2000/svg" className="pres-kpi-icon-svg">
      <defs>
        <filter id={`kpi-shadow-${color.replace('#','')}`} x="-4%" y="-4%" width="108%" height="108%" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="-2" dy="-2"/>
          <feGaussianBlur stdDeviation="2"/>
          <feColorMatrix type="matrix" values={light ? "0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.9 0" : "0 0 0 0 0.04 0 0 0 0 0.06 0 0 0 0 0.1 0 0 0 0.8 0"}/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="2" dy="2"/>
          <feGaussianBlur stdDeviation="2"/>
          <feColorMatrix type="matrix" values={light ? "0 0 0 0 0.7 0 0 0 0 0.7 0 0 0 0 0.75 0 0 0 0.5 0" : "0 0 0 0 0.15 0 0 0 0 0.2 0 0 0 0 0.35 0 0 0 0.4 0"}/>
          <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow" result="shape"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="7" dy="7"/>
          <feGaussianBlur stdDeviation="7"/>
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values={light ? "0 0 0 0 0.7 0 0 0 0 0.72 0 0 0 0 0.76 0 0 0 0.8 0" : "0 0 0 0 0.02 0 0 0 0 0.03 0 0 0 0 0.06 0 0 0 1 0"}/>
          <feBlend mode="normal" in2="shape" result="effect3_innerShadow"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="-6" dy="-6"/>
          <feGaussianBlur stdDeviation="6"/>
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values={light ? "0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.9 0" : "0 0 0 0 0.18 0 0 0 0 0.24 0 0 0 0 0.4 0 0 0 0.7 0"}/>
          <feBlend mode="normal" in2="effect3_innerShadow" result="effect4_innerShadow"/>
        </filter>
      </defs>
      <circle cx="123.5" cy="123.5" r="120.5" fill={fill} filter={`url(#kpi-shadow-${color.replace('#','')})`}/>
      <circle cx="123.5" cy="123.5" r="120.5" fill="none" stroke={color} strokeOpacity="0.18" strokeWidth="2"/>
      <g transform="translate(123.5,123.5)" fill={color} opacity="0.85">
        {children}
      </g>
    </svg>
  );
}

function SlideKPIs({ data, clock, dateStr, theme }) {
  const kpis = [
    { label: 'Total Transactions', value: data.totalTx,      suffix: '',    sub: 'last 24 hours',                           color: '#bf14cc', glow: 'rgba(191,20,204,0.25)',
      icon: <><rect x="-28" y="-22" width="56" height="10" rx="5"/><rect x="-28" y="-6" width="40" height="10" rx="5"/><rect x="-28" y="10" width="48" height="10" rx="5"/></> },
    { label: 'TX Success Rate',    value: data.successRate,  suffix: '%',   sub: `${data.successCount.toLocaleString()} successful`, color: '#00d97e', glow: 'rgba(0,217,126,0.22)',
      icon: <><path d="M-26,4 L-10,18 L26,-18" stroke="#00d97e" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.85"/></> },
    { label: 'Avg Response Time',  value: data.avgResponse,  suffix: 'ms',  sub: 'across all services',                     color: '#ff7c43', glow: 'rgba(255,124,67,0.22)',
      icon: <><circle cx="0" cy="0" r="22" stroke="#ff7c43" strokeWidth="10" fill="none" opacity="0.85"/><rect x="-5" y="-22" width="10" height="22" rx="5"/><rect x="0" y="-5" width="20" height="10" rx="5"/></> },
    { label: 'Open Incidents',     value: data.openIncidents,suffix: '',    sub: `${data.criticalIncidents} critical`,       color: '#ff4d6d', glow: 'rgba(255,77,109,0.22)',
      icon: <><path d="M0,-28 L24,20 H-24 Z" stroke="#ff4d6d" strokeWidth="10" strokeLinejoin="round" fill="none" opacity="0.85"/><rect x="-5" y="-8" width="10" height="18" rx="4"/><circle cx="0" cy="16" r="5"/></> },
    { label: 'Active Alerts',      value: data.activeAlerts, suffix: '',    sub: `${data.criticalAlerts} critical in alarm`, color: '#ffb830', glow: 'rgba(255,184,48,0.22)',
      icon: <><path d="M0,-26 C12,-26 22,-16 22,-4 C22,10 12,22 0,28 C-12,22 -22,10 -22,-4 C-22,-16 -12,-26 0,-26 Z" stroke="#ffb830" strokeWidth="9" fill="none" opacity="0.85"/><rect x="-4" y="-12" width="8" height="16" rx="4"/><circle cx="0" cy="12" r="5"/></> },
    { label: 'PIR Pending',        value: data.pirPending,   suffix: '',    sub: 'post-incident reviews due',                color: '#d94fe0', glow: 'rgba(217,79,224,0.22)',
      icon: <><circle cx="0" cy="-8" r="14" stroke="#d94fe0" strokeWidth="9" fill="none" opacity="0.85"/><rect x="-4" y="8" width="8" height="20" rx="4"/></> },
  ];
  return (
    <div className="pres-slide">
      <div className="pres-slide-header">
        <div>
          <div className="pres-eyebrow">Operations Dashboard · Live</div>
          <div className="pres-title">System <span className="pres-title-grad">Overview</span></div>
          <div className="pres-subtitle">Command Quality — All Components</div>
        </div>
        <div className="pres-clock-block">
          <div className="pres-clock">{clock}</div>
          <div className="pres-date">{dateStr}</div>
        </div>
      </div>
      <div className="pres-kpi-grid">
        {kpis.map(k => (
          <div key={k.label} className="pres-kpi-card" style={{'--kc':k.color,'--kg':k.glow}}>
            <div className="pres-kpi-label">{k.label}</div>
            <div className="pres-kpi-value" style={{color:k.color}}>
              <Counter value={k.value} suffix={k.suffix} />
            </div>
            <div className="pres-kpi-sub">{k.sub}</div>
            <div className="pres-kpi-icon"><KpiIcon color={k.color} light={theme==='light'}>{k.icon}</KpiIcon></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlideTxHealth({ data, theme }) {
  const rings = [
    { pct: data.kpis.successRate, color:'#00d97e', dim:'rgba(0,217,126,0.1)',  label:'2xx Success',       sub:`${data.kpis.successCount.toLocaleString()} req` },
    { pct: 3.4,                   color:'#ffb830', dim:'rgba(255,184,48,0.1)', label:'4xx Client Errors', sub:'30 requests' },
    { pct: 3.3,                   color:'#ff4d6d', dim:'rgba(255,77,109,0.1)', label:'5xx Server Errors', sub:'56 requests' },
  ];
  return (
    <div className="pres-slide">
      <div className="pres-slide-header">
        <div>
          <div className="pres-eyebrow">Transaction Health</div>
          <div className="pres-title">Volume & <span className="pres-title-grad">Status</span></div>
        </div>
        <div className="pres-stat-chips">
          <div className="pres-stat-chip" style={{'--cc':'#bf14cc'}}>
            <span className="pres-stat-chip-val">{data.kpis.totalTx.toLocaleString()}</span>
            <span className="pres-stat-chip-lbl">total tx</span>
          </div>
          <div className="pres-stat-chip" style={{'--cc':'#ff7c43'}}>
            <span className="pres-stat-chip-val">{data.kpis.avgResponse}ms</span>
            <span className="pres-stat-chip-lbl">avg latency</span>
          </div>
        </div>
      </div>
      <div className="pres-tx-layout">
        <div className="pres-card pres-card--tall">
          <div className="pres-card-label">Transaction Volume — Last 24 Hours</div>
          <div className="pres-chart-stretch">
            <VolumeChart data={data.hourlyVolume}/>
          </div>
        </div>
        <div className="pres-rings-col">
          {rings.map(r => (
            <div key={r.label} className="pres-ring-card">
              <GlowRing pct={r.pct} color={r.color} dim={r.dim} size={160} label={r.label} sublabel={r.sub} light={theme==='light'}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SlideComponents({ components }) {
  const maxReq = Math.max(...components.map(c=>c.requests),1);
  const [animPct, setAnimPct] = useState(0);
  useEffect(()=>{
    setAnimPct(0);
    const dur=1000, start=performance.now();
    const tick=now=>{ const p=Math.min((now-start)/dur,1); setAnimPct(1-Math.pow(1-p,3)); if(p<1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
  },[]);
  return (
    <div className="pres-slide">
      <div className="pres-slide-header">
        <div>
          <div className="pres-eyebrow">Infrastructure</div>
          <div className="pres-title">Component <span className="pres-title-grad">Health</span></div>
        </div>
      </div>
      <div className="pres-comp-layout">
        <div className="pres-card">
          <div className="pres-card-label">Health Score per Component</div>
          <div className="pres-comp-list">
            {components.map(c => (
              <div key={c.name} className="pres-comp-item">
                <div className="pres-comp-meta">
                  <span className="pres-comp-name">{c.name}</span>
                  <span className="pres-comp-pct" style={{color:c.color}}>{c.health}%</span>
                </div>
                <SegBar pct={c.health} color={c.color} dim={c.dim} height={14} animPct={animPct}/>
              </div>
            ))}
          </div>
        </div>
        <div className="pres-card">
          <div className="pres-card-label">Request Volume per Component</div>
          <div className="pres-req-list">
            {components.map(c => (
              <div key={c.name} className="pres-req-item">
                <div className="pres-req-meta">
                  <span className="pres-comp-name">{c.name}</span>
                  <span className="pres-req-count" style={{color:c.color}}>{c.requests}</span>
                </div>
                <div className="pres-req-track">
                  <div className="pres-req-fill" style={{
                    width:`${(c.requests/maxReq)*100*animPct}%`,
                    background:`linear-gradient(90deg,${c.color},${c.color}88)`,
                    boxShadow:`0 0 12px ${c.color}55`
                  }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlideIncidentsAlerts({ incidents, alerts }) {
  return (
    <div className="pres-slide">
      <div className="pres-slide-header">
        <div>
          <div className="pres-eyebrow">Monitoring</div>
          <div className="pres-title">Incidents & <span className="pres-title-grad">Alerts</span></div>
        </div>
        <div className="pres-stat-chips">
          <div className="pres-stat-chip" style={{'--cc':'#ff4d6d'}}>
            <span className="pres-stat-chip-val">{incidents.length}</span>
            <span className="pres-stat-chip-lbl">open incidents</span>
          </div>
          <div className="pres-stat-chip" style={{'--cc':'#ffb830'}}>
            <span className="pres-stat-chip-val">{alerts.length}</span>
            <span className="pres-stat-chip-lbl">active alerts</span>
          </div>
        </div>
      </div>
      <div className="pres-ia-layout">
        <div className="pres-card">
          <div className="pres-card-label">Open Incidents</div>
          <div className="pres-ia-list">
            {incidents.map((inc,i)=>(
              <div key={i} className="pres-ia-item" style={{'--ic':sevColor(inc.severity)}}>
                <div className="pres-ia-sev-bar" style={{background:sevColor(inc.severity)}}/>
                <div className="pres-ia-dot pres-pulse" style={{background:sevColor(inc.severity),boxShadow:`0 0 10px ${sevColor(inc.severity)}`}}/>
                <div className="pres-ia-text">
                  <div className="pres-ia-title">{inc.id} — {inc.title}</div>
                  <div className="pres-ia-sub">{inc.service} · {inc.ago}</div>
                </div>
                <div className="pres-ia-tags">
                  <span className={`pres-b pres-b--${sevClass(inc.severity)}`}>{inc.severity}</span>
                  <span className="pres-b pres-b--med">{inc.status.replace('_',' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="pres-card">
          <div className="pres-card-label">Active Alerts</div>
          <div className="pres-ia-list">
            {alerts.map((al,i)=>(
              <div key={i} className="pres-ia-item" style={{'--ic':sevColor(al.severity)}}>
                <div className="pres-ia-sev-bar" style={{background:sevColor(al.severity)}}/>
                <div className="pres-ia-dot pres-pulse" style={{background:sevColor(al.severity),boxShadow:`0 0 10px ${sevColor(al.severity)}`}}/>
                <div className="pres-ia-text">
                  <div className="pres-ia-title">{al.title}</div>
                  <div className="pres-ia-sub">{al.service}</div>
                </div>
                <div className="pres-ia-tags">
                  <span className={`pres-b pres-b--${sevClass(al.severity)}`}>{al.severity}</span>
                  <span className="pres-b pres-b--crit">{al.state}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlidePipeline({ prEvents, prTrend }) {
  const passed = prEvents.filter(p=>p.status==='PASSED').length;
  const failed  = prEvents.filter(p=>p.status==='FAILED').length;
  const pending = prEvents.filter(p=>p.status==='PENDING').length;
  return (
    <div className="pres-slide">
      <div className="pres-slide-header">
        <div>
          <div className="pres-eyebrow">Pipeline</div>
          <div className="pres-title">PR <span className="pres-title-grad">Events</span></div>
        </div>
        <div className="pres-stat-chips">
          <div className="pres-stat-chip" style={{'--cc':'#00d97e'}}><span className="pres-stat-chip-val">{passed}</span><span className="pres-stat-chip-lbl">passed</span></div>
          <div className="pres-stat-chip" style={{'--cc':'#ff4d6d'}}><span className="pres-stat-chip-val">{failed}</span><span className="pres-stat-chip-lbl">failed</span></div>
          <div className="pres-stat-chip" style={{'--cc':'#ffb830'}}><span className="pres-stat-chip-val">{pending}</span><span className="pres-stat-chip-lbl">pending</span></div>
        </div>
      </div>
      <div className="pres-pipeline-layout">
        <div className="pres-card pres-card--tall">
          <div className="pres-card-label">Pass / Fail Trend — Last 7 Days</div>
          <div className="pres-legend">
            {[['#00d97e','Passed'],['#ff4d6d','Failed']].map(([c,l])=>(
              <div key={l} className="pres-legend-item">
                <div style={{width:24,height:3,background:c,borderRadius:2,boxShadow:`0 0 6px ${c}`}}/>
                <span>{l}</span>
              </div>
            ))}
          </div>
          <div className="pres-chart-stretch">
            <PrTrendChart trend={prTrend}/>
          </div>
        </div>
        <div className="pres-card">
          <div className="pres-card-label">Recent PR Events</div>
          <div className="pres-ia-list">
            {prEvents.map((pr,i)=>{
              const c = pr.status==='PASSED'?'#00d97e':pr.status==='FAILED'?'#ff4d6d':'#ffb830';
              return (
                <div key={i} className="pres-ia-item" style={{'--ic':c}}>
                  <div className="pres-ia-sev-bar" style={{background:c}}/>
                  <div className="pres-ia-author">{(pr.author||'?').slice(0,2).toUpperCase()}</div>
                  <div className="pres-ia-text">
                    <div className="pres-ia-title">{pr.repo} #{pr.number} — {pr.title}</div>
                    <div className="pres-ia-sub">{pr.author} · {pr.ago}</div>
                  </div>
                  <span className={`pres-b pres-b--${stClass(pr.status)}`}>{pr.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function PresentationPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [slide, setSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [clock, setClock] = useState('');
  const [dateStr, setDateStr] = useState('');
  const progressRef = useRef(0);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClock(now.toLocaleTimeString('en-ZA'));
      setDateStr(now.toLocaleDateString('en-ZA',{weekday:'long',day:'numeric',month:'long',year:'numeric'}));
    };
    tick(); const t = setInterval(tick,1000); return ()=>clearInterval(t);
  },[]);

  const SLIDES = useMemo(()=>[
    { label:'Overview',   icon:'⬡', component:<SlideKPIs data={MOCK.kpis} clock={clock} dateStr={dateStr} theme={theme}/> },
    { label:'TX Health',  icon:'◈', component:<SlideTxHealth data={{hourlyVolume:MOCK.hourlyVolume,kpis:MOCK.kpis}} theme={theme}/> },
    { label:'Components', icon:'◉', component:<SlideComponents components={MOCK.components}/> },
    { label:'Incidents',  icon:'◆', component:<SlideIncidentsAlerts incidents={MOCK.incidents} alerts={MOCK.alerts}/> },
    { label:'Pipeline',   icon:'◀', component:<SlidePipeline prEvents={MOCK.prEvents} prTrend={MOCK.prTrend}/> },
  ],[clock,dateStr]);

  const goTo = useCallback((idx)=>{
    setTransitioning(true);
    setTimeout(()=>{ setSlide(idx); setProgress(0); progressRef.current=0; setTransitioning(false); },300);
  },[]);
  const next = useCallback(()=>goTo((slide+1)%SLIDES.length),[slide,SLIDES.length,goTo]);
  const prev = useCallback(()=>goTo((slide-1+SLIDES.length)%SLIDES.length),[slide,SLIDES.length,goTo]);

  useEffect(()=>{
    if(paused) return;
    const step=50;
    const id=setInterval(()=>{
      progressRef.current += (step/SLIDE_DURATION)*100;
      setProgress(progressRef.current);
      if(progressRef.current>=100) next();
    },step);
    return ()=>clearInterval(id);
  },[slide,paused,next]);

  useEffect(()=>{
    const h=e=>{
      if(e.key==='ArrowRight'||e.key===' ') next();
      else if(e.key==='ArrowLeft') prev();
      else if(e.key==='Escape') navigate('/dashboard');
      else if(e.key==='p'||e.key==='P') setPaused(p=>!p);
    };
    window.addEventListener('keydown',h); return ()=>window.removeEventListener('keydown',h);
  },[next,prev,navigate]);

  return (
    <div className={`pres-shell${theme === 'light' ? ' pres-light' : ''}`}>
      <Particles/>

      {/* top bar */}
      <div className="pres-topbar">
        <div className="pres-brand">
          <div className="pres-brand-icon">CQ</div>
          <div>
            <div className="pres-brand-name">Command Quality</div>
            <div className="pres-brand-sub">Operations Platform · Live</div>
          </div>
        </div>

        <div className="pres-nav-dots">
          {SLIDES.map((s,i)=>(
            <button key={i} className={`pres-nav-dot${i===slide?' pres-nav-dot--active':''}`}
              onClick={()=>goTo(i)}>
              <span className="pres-nav-dot-icon">{s.icon}</span>
              <span className="pres-nav-dot-label">{s.label}</span>
            </button>
          ))}
        </div>

        <div className="pres-topbar-actions">
          <button className="pres-action-btn" onClick={()=>setPaused(p=>!p)} title="P to pause">
            {paused?'▶':'⏸'}
          </button>
          <button className="pres-action-btn" onClick={prev}>‹</button>
          <button className="pres-action-btn" onClick={next}>›</button>
          <button className="pres-exit-btn" onClick={()=>navigate('/dashboard')}>✕ Exit</button>
        </div>
      </div>

      {/* progress */}
      <div className="pres-progress-track">
        <div className="pres-progress-fill" style={{width:`${progress}%`}}/>
      </div>

      {/* slide */}
      <div className={`pres-stage${transitioning?' pres-stage--out':''}`}>
        {SLIDES[slide].component}
      </div>

      {/* bottom */}
      <div className="pres-footer">
        <button className="pres-foot-btn" onClick={prev}>← Prev</button>
        <div className="pres-foot-indicators">
          {SLIDES.map((_,i)=>(
            <div key={i} className={`pres-foot-dot${i===slide?' pres-foot-dot--active':''}`}
              onClick={()=>goTo(i)}/>
          ))}
        </div>
        <button className="pres-foot-btn" onClick={next}>Next →</button>
      </div>
    </div>
  );
}
